import { useContext, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import pick from "lodash/pick";
import { InternalButton } from "@sharedComponents/buttons";

import { useStoreContext } from "@components/store/provider";
import { ProductListingContext } from "@components/store/products/listing/listing-provider";

import useWithAnalytics from "@stadium/services/analytics/hooks/use-with-analytics";
import { showErrorToast } from "@stadium/services/react-toastify";

import { useProductDetailContext } from "@contexts/product-detail-context";
import { usePreviewModeContext } from "@contexts/preview-mode-context";
import { useSendItemsContext } from "@contexts/send-items-context";

import useRouter from "@stadium/hooks/lib/use-custom-route";
import usePopulateItems from "@hooks/cart/use-populate-items";
import usePopulateLocalItems from "@hooks/cart/use-populate-local-items";

import {
  getCartOrder,
  getCartOrderLineItems,
  getCartOrderProductSets,
  getReceivedItemsCount,
} from "@store/selectors/cart";
import { getIsPricelessShop } from "@selectors/store";
import { getCountry } from "@selectors/country";

import {
  isCollectionTypeProduct,
  isMedleyProduct,
  isThemedBoxProduct,
  isSwagProduct,
  getProductType,
  getProductCategory,
} from "@helpers/products/types";
import { getUpdatedLocalCartQuantity } from "@helpers/products/tile";
import {
  extractColorAndSizeFromVariant,
  getCurrentQtyOfProductInCart,
  getLineItemImage,
  getRestrictionIdsString,
  mapDietaryRestrictions,
  getOptionRowsFromVariant,
  getCleanedAttributes,
} from "@helpers/orders/cart";
import { getProductFromBoxList, handleQuantityOfProductInBox } from "@helpers/orders/byo-box";
import { getPriceFromBulkProduct } from "@stadium/helpers/product/utils";

import { UPDATE_ORDER_ERROR_TOAST_ID } from "@constants/toast-ids";
import { getCartRoute } from "@constants/routes";
import {
  LOCKER_PRODUCT,
  ON_DEMAND_PRODUCT,
  OPTIONTYPE_PRICE_KEY,
  OPTIONTYPE_PACK_KEY,
} from "@constants/products/detail";
import {
  ANALYTICS_TARGET_ADD_TO_BAG,
  ANALYTICS_TARGET_ADDED_TO_BAG,
} from "@constants/analytics/shop";
import { ANALYTICS_EVENT_ACTION_VIEWED } from "@stadium/services/analytics/constants/default";

import styles from "./main.module.scss";

export const buildAddToBagSelectedAttributes = (
  variant,
  dietaryPreference = "",
  dietaryRestrictions = [],
) => {
  const optionRows = getOptionRowsFromVariant(variant);
  const { color: colorOption, size: sizeOption } = extractColorAndSizeFromVariant(optionRows);
  const rewardsOption = optionRows?.find((ov) => ov.optionType?.key === OPTIONTYPE_PRICE_KEY);
  const packOption = optionRows?.find((ov) => ov.optionType?.key === OPTIONTYPE_PACK_KEY);
  const preferenceStr = String(dietaryPreference ?? "").trim();

  const selectedAttributes = {
    sizes: sizeOption?.presentation ? [sizeOption.presentation] : undefined,
    colors: colorOption?.presentation ? [colorOption.presentation] : undefined,
    rewards: rewardsOption?.presentation,
    pack: packOption?.presentation,
    dietaryPreference: preferenceStr || undefined,
    dietaryRestrictions: mapDietaryRestrictions(dietaryRestrictions),
  };

  return getCleanedAttributes(selectedAttributes);
};

const AddToBagBtn = ({
  product,
  quantity,
  variant,
  restrictions,
  preferences = "",
  onSuccessCallback = () => {},
  btnParentClass = styles["bagBtnBlock"],
  btnClass = "",
  isDisabled = false,
  showAddToBox,
  handleQuantity,
  toggleRestrictionsModal,
  isColorAndSizeSelectionAvailable = true,
}) => {
  const {
    intent,
    name,
    id,
    priceIn,
    productCatalogSlug,
    currentCatalogExternalStoreId,
    lockerProduct = false,
  } = product;

  const router = useRouter();

  const { storeBasePath } = useStoreContext();

  const {
    addItemInCart,
    addMultipleItemsInCart,
    addCollectionItemInCart,
    updateCollectionItemInCart,
    loading,
  } = usePopulateItems();

  const { addItemInLocalCart, deleteAndAddItemInCart } = usePopulateLocalItems();

  const order = useSelector(getCartOrder);
  const productSets = useSelector(getCartOrderProductSets);
  const lineItems = useSelector(getCartOrderLineItems);
  const defaultCountry = useSelector(getCountry);

  const { isSendToMyself } = useSendItemsContext();

  const { isGuestOrPreviewMode } = usePreviewModeContext();
  const { boxProductList, handleBoxProductList } = useContext(ProductListingContext);
  const {
    toggleAddedToBagModal,
    isOptionSelected,
    isColorSelected,
    setOptionSelectionError,
    setColorSelectionError,
    editProductIds: [editProductId],
  } = useProductDetailContext();

  const withAnalytics = useWithAnalytics();

  const disabled = loading || !quantity || isDisabled;

  const btnText = useMemo(() => {
    if (showAddToBox) return "Add to Box";
    if (editProductId) return "Update Bag";
    return "Add to Bag";
  }, [showAddToBox, editProductId]);

  useEffect(() => {
    if (boxProductList?.length) {
      const productInBoxList = getProductFromBoxList({ productId: id, boxProductList });
      handleQuantity(productInBoxList?.count);
    }
  }, [boxProductList]);

  const addProduct = (payload) => {
    if (isCollectionTypeProduct(intent)) {
      const productSet = productSets.find(({ id }) => id === editProductId);
      if (editProductId && productSet) {
        return updateCollectionItemInCart({
          orderProductSetId: productSet.id,
          quantity: payload.quantity,
          orderProductSetRestrictionId: getRestrictionIdsString(productSet),
          intent,
          dietaryPreference: payload.dietaryPreference,
          dietaryRestrictions: payload.dietaryRestrictions,
          restrictions: payload.restrictions,
          showToast: false,
          zoneId: payload.zoneId,
          itemType: payload.itemType,
          ...pick(payload, "price", "storePrice", "isRestrictionsSelected"),
        });
      }
      return addCollectionItemInCart(payload);
    }
    const { productId, storeId } = lineItems.find(({ id }) => id === editProductId) || {};
    if (
      productId &&
      (String(productId) !== payload.productId || String(storeId) !== String(payload.storeId))
    )
      return addMultipleItemsInCart(
        [
          payload,
          {
            productId: productId.toString(),
            quantity: 0,
            storeId: storeId,
            zoneId: payload.zoneId || "",
          },
        ],
        intent,
      );
    return addItemInCart(payload);
  };

  const addPreviewModeProduct = (payload) => {
    const previewModePayload = {
      ...payload,
      variant: {
        ...variant,
        name,
        slug: productCatalogSlug,
        restaurantName: product?.brand?.displayName,
        image: getLineItemImage(variant, product),
        shippedSeparately: product?.shippedSeparately || false,
      },
    };
    if (isSwagProduct(payload.intent)) {
      previewModePayload.product = product;
    }
    if (isCollectionTypeProduct(intent)) {
      // make product Id unique as we create new product set on add to bag in BE
      previewModePayload.productId = `${previewModePayload.productId}-${
        variant.priceIn
      }-${Date.now()}`;
      previewModePayload.isCollectionType = true;
      previewModePayload.askForRestriction = product?.askForRestriction || false;
      previewModePayload.askForPreference = product?.askForPreference || false;
    }

    // unique id use as lineItemId
    // in login flow for collection product lineItemid remains same on variant change, implement same behaviour here
    previewModePayload.id =
      previewModePayload.isCollectionType && editProductId
        ? editProductId
        : previewModePayload.productId;

    if (editProductId) {
      const { productId, storeId } = lineItems.find(({ id }) => id === editProductId) || {};
      if (
        productId &&
        (productId.toString() !== payload.productId || storeId !== payload.storeId)
      ) {
        return deleteAndAddItemInCart({
          oldProductId: productId,
          oldStoreId: storeId,
          newPayload: previewModePayload,
        });
      }
    }

    return addItemInLocalCart(previewModePayload);
  };

  const getUnitPriceFromTable = () => {
    if (product?.hasTierPricing || product?.masterVariant?.prices?.length > 1) {
      return getPriceFromBulkProduct(product.masterVariant, quantity);
    }

    return null;
  };

  const totalPrice = () => {
    const unitPrice = getUnitPriceFromTable();
    return unitPrice ? unitPrice * quantity : null;
  };

  const onAddToBagSuccess = () => {
    toggleAddedToBagModal({
      product,
      quantity,
      variant,
      totalPrice: totalPrice(),
      isCheckoutBtnRequired: isSendToMyself,
    });
    onSuccessCallback();
  };

  const addToBag = () => {
    const productId = variant?.externalId;
    const zoneId = defaultCountry?.zoneId || "";
    const vendorIdOption = variant.vendorId || variant.vendorByCountry?.externalId;
    const vendorId = isSwagProduct(intent) ? vendorIdOption : null;
    const payload = {
      productId,
      storePrice: getUnitPriceFromTable() || variant?.priceIn || product.priceIn,
      intent: intent,
      dietaryPreference: preferences,
      quantity:
        getUpdatedLocalCartQuantity({
          quantity,
          isGuestOrPreviewMode,
          productId,
          storeId: currentCatalogExternalStoreId,
          isEditMode: !!editProductId,
          lineItems,
        }) +
        (order.id
          ? getCurrentQtyOfProductInCart({
              order,
              productId,
              currentCatalogExternalStoreId,
              editProductId,
              zoneId,
            })
          : 0),
      vendorId,
      storeId: currentCatalogExternalStoreId,
      zoneId,
      itemType: lockerProduct ? LOCKER_PRODUCT : ON_DEMAND_PRODUCT,
    };

    if (isMedleyProduct(intent)) {
      payload.restrictions = restrictions?.map((r) => r.id || r).join(",");
      payload.isRestrictionsSelected = true;
    }
    if (isThemedBoxProduct(intent)) {
      payload.dietaryRestrictions = restrictions?.map((r) => r.id || r);
    }
    (isGuestOrPreviewMode ? addPreviewModeProduct(payload) : addProduct(payload))
      .then(() => {
        if (editProductId) router.push(getCartRoute({ storeBasePath }));
        else {
          const selectedAttributes = buildAddToBagSelectedAttributes(
            variant,
            preferences,
            restrictions,
          );
          withAnalytics(onAddToBagSuccess, {
            target: ANALYTICS_TARGET_ADDED_TO_BAG,
            action: ANALYTICS_EVENT_ACTION_VIEWED,
            track: true,
            eventProperties: {
              product_id: id,
              quantity,
              selected_attributes: selectedAttributes,
              product_type: getProductType(product),
              category: getProductCategory(product),
            },
          })();
        }
      })
      .catch((err) => {
        const errors = err?.response?.data?.errors;
        if (errors?.length) {
          errors.forEach((err) =>
            showErrorToast({
              message: err.detail,
              config: { toastId: UPDATE_ORDER_ERROR_TOAST_ID },
            }),
          );
        }
      });
  };

  const addToBox = ({ externalId, newQuantity, product }) => {
    handleQuantityOfProductInBox({
      externalId,
      newQuantity,
      product,
      boxProductList,
      handleBoxProductList,
      addQuantityFlag: true,
    });
    onSuccessCallback();
  };
  const handleAddToBox = () => {
    const data = {
      id: id,
      name: name,
      prices: [variant?.priceIn || priceIn],
    };

    addToBox({
      externalId: variant?.externalId,
      newQuantity: quantity,
      product: data,
    });
  };

  const handleAddButton = () => {
    if (showAddToBox) handleAddToBox();
    else if (toggleRestrictionsModal) toggleRestrictionsModal();
    else if (isColorAndSizeSelectionAvailable && isSwagProduct(intent) && !isColorSelected)
      setColorSelectionError(true);
    else if (isColorAndSizeSelectionAvailable && !isOptionSelected) setOptionSelectionError(true);
    else {
      const selectedAttributes = buildAddToBagSelectedAttributes(
        variant,
        preferences,
        restrictions,
      );
      withAnalytics(undefined, {
        target: ANALYTICS_TARGET_ADD_TO_BAG,
        track: true,
        eventProperties: {
          product_id: id,
          quantity,
          selected_attributes: selectedAttributes,
          product_type: getProductType(product),
          category: getProductCategory(product),
        },
      })();
      addToBag();
    }
  };
  return (
    <div className={btnParentClass}>
      <InternalButton
        className={styles.addToBagBtn}
        onClick={handleAddButton}
        isLoading={loading}
        disabled={disabled}
        styleType="primary"
      >
        {btnText}
      </InternalButton>
    </div>
  );
};

const WrappedAddToBagBtn = (props) => {
  const isPricelessShop = useSelector(getIsPricelessShop);
  const isRLPFlow = useSelector(getReceivedItemsCount);
  if (isPricelessShop && !isRLPFlow) return;
  return <AddToBagBtn {...props} />;
};

export default WrappedAddToBagBtn;
